<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useGalleryStore } from '@/stores';
import { useQuasar } from 'quasar';
import { PageHeader, PageFooter } from '@/components/clients/common';
import { one, three, five } from '@/assets/grid';
import { hexToCSSFilter } from 'hex-to-css-filter';
import Tr from '@/i18n/translation';
import { WishInfoIcon, PlayerIcon } from '.';
import { DashPlayer } from '@/components/clients/gallery';
import { useEventStore } from '@/stores/event/event.store';

const config = {
  acceptanceLossPercentage: 1,
  maxChecks: 10,
};

const eventStore = useEventStore();
const galleryStore = useGalleryStore();
const quasar = useQuasar();

const mainColor = ref(eventStore.getColors.mainColor);
const backgroundColor = ref(eventStore.getColors.backgroundColor);

const cssFilter = ref(null);

const numberOfColumns = ref(0);
const minNumOfColumns = ref(3);
const screenColumns = ref(0);
const iconNumOfColumns = ref(three);

const isScrollActive = ref(true);
const columnId = ref(0);

const videoName = computed(() => {
  const name = galleryStore.expandedName;
  const parts = name.split('.');
  parts.pop();
  return parts.join('.');
});

const isNumOfColumnsVisible = computed(() => {
  return screenColumns.value < 4;
});

watch(
  () => galleryStore.numberOfImages,
  () => {
    columnId.value++;
  },
);

watch(numberOfColumns, () => {
  if (screenColumns.value === 1) {
    switch (minNumOfColumns.value) {
      case 1:
        iconNumOfColumns.value = three;
        break;
      case 3:
        iconNumOfColumns.value = five;
        break;
      case 5:
        iconNumOfColumns.value = one;
        break;
      default:
        iconNumOfColumns.value = three;
    }
  } else {
    switch (minNumOfColumns.value) {
      case 3:
        iconNumOfColumns.value = five;
        break;
      case 5:
        iconNumOfColumns.value = three;
        break;
      default:
        iconNumOfColumns.value = three;
    }
  }
});

function componentToHex(c) {
  const hex = c.toString(16);
  return hex.length == 1 ? '0' + hex : hex;
}

function checkScreen() {
  screenColumns.value = parseInt(window.innerWidth / 250);
  numberOfColumns.value = isNumOfColumnsVisible.value
    ? minNumOfColumns.value
    : screenColumns.value;
  columnId.value++;
}

async function handleScroll() {
  const scrollPositionFromBottom =
    document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
  const distanceFromBottomToTriggerAction = 150;

  if (scrollPositionFromBottom <= distanceFromBottomToTriggerAction) {
    const numAddedImages = await galleryStore.loadImages();
    if (numAddedImages === 0) {
      window.removeEventListener('scroll', handleScroll);
      isScrollActive.value = false;
    }
  }
}

function getElements(n) {
  return galleryStore.getElements(n, numberOfColumns.value);
}

async function getPicture() {
  const numAddedImages = await galleryStore.loadImages();
  if (numAddedImages !== 0) {
    if (!isScrollActive.value) {
      window.addEventListener('scroll', handleScroll);
      isScrollActive.value = true;
    }
    quasar.notify({
      type: 'positive',
      message: Tr.t('appCore.apps.event.gallery.loadedFiles'),
    });
  } else {
    quasar.notify({
      type: 'negative',
      message: Tr.t('appCore.apps.event.gallery.noNewFiles'),
    });
  }
}

function changeColumnsNumber() {
  minNumOfColumns.value += 2;
  if (minNumOfColumns.value > 5) {
    if (screenColumns.value === 1) {
      minNumOfColumns.value = 1;
    } else {
      minNumOfColumns.value = 3;
    }
  }
  checkScreen();
}

onMounted(() => {
  window.addEventListener('resize', checkScreen);
  window.addEventListener('scroll', handleScroll);
  checkScreen();
  galleryStore.loadImages();
  isScrollActive.value = true;

  if (mainColor.value[0] !== '#') {
    const values = mainColor.value.match(/\d+/g);
    const [r, g, b] = values.map(Number);
    mainColor.value =
      '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
  }
  cssFilter.value = hexToCSSFilter(mainColor.value, config).filter.slice(0, -1);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreen);
  window.removeEventListener('scroll', handleScroll);
  isScrollActive.value = false;
});
</script>

<template>
  <div class="background">
    <div class="content">
      <PageHeader
        :main-color="mainColor"
        :background-color="backgroundColor"
        :header-text="$t('appCore.apps.event.gallery.name')"
      >
        <button
          v-if="isNumOfColumnsVisible"
          class="grid-num"
          @click="changeColumnsNumber"
        >
          <p class="grid-button-text">
            {{ $t('appCore.apps.event.gallery.changeView') }}
          </p>
          <img class="icon" :src="iconNumOfColumns" />
        </button>
      </PageHeader>
      <div class="gallery">
        <div v-for="n in numberOfColumns" :key="n + columnId" class="column">
          <div
            v-for="(image, i) in getElements(n - 1)"
            :key="n * i"
            class="box"
            @click="galleryStore.toggleImage(i * numberOfColumns + (n - 1))"
          >
            <WishInfoIcon
              v-if="galleryStore.isWishById(i * numberOfColumns + (n - 1))"
              class="wish-info"
              :color="backgroundColor"
            />
            <PlayerIcon
              v-if="galleryStore.isVideoById(i * numberOfColumns + (n - 1))"
              class="video-info"
              :color="backgroundColor"
            />
            <img
              :key="'img' + i"
              :class="{ image: image.url !== '' }"
              :src="image.url"
              alt=""
            />
            <div v-if="image.url === ''" class="image-dots">
              <div class="loading-dots">
                <span class="dot dot1"></span>
                <span class="dot dot2"></span>
                <span class="dot dot3"></span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="galleryStore.isExpandedAny" class="expanded">
          <DashPlayer
            v-if="galleryStore.isVideo"
            :key="videoName"
            :video-name="videoName"
            class="image image-expanded video-expanded"
            :class="{ 'image-expanded-wish': galleryStore.isWish }"
            alt=""
          />
          <img
            v-else
            class="image image-expanded"
            :class="{ 'image-expanded-wish': galleryStore.isWish }"
            :src="galleryStore.expandedImage"
            alt=""
            loading="lazy"
            @click="galleryStore.resetExpanded()"
          />
          <div v-if="galleryStore.isWish" class="expanded-wish">
            <p>{{ galleryStore.wish }}</p>
          </div>
          <button
            class="button left-button left"
            @click="galleryStore.previousImage()"
          >
            &lt;
          </button>
          <div class="overlay"></div>
          <div class="right">
            <button
              class="button right-button"
              @click="galleryStore.resetExpanded()"
            >
              X
            </button>
            <button
              class="button right-button right-button-next"
              @click="galleryStore.nextImage()"
            >
              >
            </button>
            <div class="right-space"></div>
          </div>
        </div>
      </div>
      <div>
        <div class="bottom">
          <div v-if="galleryStore.isLoading" class="loading-dots">
            <span class="dot dot1"></span>
            <span class="dot dot2"></span>
            <span class="dot dot3"></span>
          </div>
          <button v-else class="button more" @click="getPicture">
            {{ $t('appCore.apps.event.gallery.moreFiles') }}
          </button>
        </div>
        <PageFooter class="footer" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.content {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.grid-num {
  right: 5%;
  z-index: 13;
  min-width: 37px;
  min-height: 37px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-left: auto;

  .grid-button-text {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 0.6em;
    max-width: 45px;
    text-align: right;
    color: v-bind(mainColor);
  }

  .icon {
    width: 37px;
    height: 37px;
    filter: v-bind(cssFilter);
    transition: 1s ease all;
  }
}

.button {
  border-radius: 48.707px;
  border: 2px solid v-bind(mainColor);
  color: v-bind(mainColor);
  padding: 15px 25px;
  font-size: 25px;
  white-space: nowrap;
  text-align: center;
  text-transform: uppercase;
  transition: background-color 1s ease;
  z-index: 15;

  &:hover {
    background-color: v-bind(mainColor);
    color: v-bind(backgroundColor);
  }

  @media screen and (max-width: $media-phone) {
    font-size: 18px;
    line-height: 30px;
    padding: 10px 20px;
  }
}

.bottom {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2% 0 3%;

  .more {
    z-index: 10;
  }
}

.gallery {
  position: relative;
  padding: 2% 3%;
  padding-top: 125px;
  width: auto;
  height: auto;
  display: grid;
  grid-template-columns: repeat(v-bind(numberOfColumns), 1fr);
  gap: 5px;
  place-items: center;

  .column {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 100%;
  }

  .box {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: auto;
    transition: opacity 0.3s ease-in-out;
  }
}

.expanded {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 12;
  width: 100vw;
  height: 100vh;
  background: rgb(0 0 0 / 70%);
  display: flex;
  flex-direction: row;
  align-items: center;

  .expanded-wish {
    position: fixed;
    width: 85%;
    height: 40%;
    display: flex;
    justify-content: center;
    align-items: end;
    bottom: 10%;
    left: 50%;
    transform: translate(-50%, 0%);
    z-index: 13;
    color: white;
    font-size: 1em;

    @media screen and (max-width: $media-tablet) {
      bottom: 5%;
    }

    p {
      background-color: rgb(0 0 0 / 60%);
      padding: 10px;
      border-radius: 10px;
    }
  }

  .overlay {
    width: 100vw;
    height: 100vh;
    z-index: 11;
  }

  .right-space,
  .left-button,
  .right-button {
    border: none;
    height: 80px;
    width: 100%;
    padding: 0;
  }

  .left-button,
  .right-button {
    color: white;
    font-size: 32px;
    background-color: rgb(0 0 0 / 0%);

    &:hover {
      background-color: rgb(255 255 255 / 10%);
      color: white;
    }

    @media screen and (max-width: $media-tablet) {
      &:hover {
        background-color: rgb(0 0 0 / 0%);
      }
    }
  }

  .right-button-next {
    margin-top: auto;
    margin-bottom: auto;
  }

  .left {
    margin-left: 4%;
    width: 100px;

    @media screen and (max-width: 600px) {
      margin-left: 5px;
      width: 50px;
    }
  }

  .right {
    margin-right: 4%;
    width: 100px;
    height: 95%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: start;

    @media screen and (max-width: 600px) {
      margin-right: 5px;
      width: 50px;
    }
  }
}

.image {
  max-width: 90vw;
  width: 100%;
  max-height: 125vh;
  cursor: pointer;
  transition: opacity 0.3s ease-in-out;
}

.image-expanded {
  position: fixed;
  width: auto;
  height: auto;
  display: block;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.7);
  z-index: 13;
}

.video-expanded {
  transform: translate(-50%, -50%) scale(1);
  width: 100%;
  z-index: 13;
}

.image-expanded-wish {
  top: 40%;
  max-height: 80vh;
  transform: translate(-50%, -50%) scale(0.7);
}

.wish-info {
  position: absolute;
  top: 0;
  right: 0;
  width: 20%;
  height: auto;
  padding: 5px;
  background-color: v-bind(mainColor);

  @media screen and (max-width: $media-phone) {
    padding: 0.2em;
  }
}

.video-info {
  position: absolute;
  top: 50;
  right: 50;
  width: 20%;
  height: auto;
  cursor: pointer;
}

.background {
  background-color: v-bind(backgroundColor);
}

.image-dots {
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 1000px) {
    height: calc(300px / v-bind(numberOfColumns));
  }
}

.loading-dots {
  text-align: center;
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.dot {
  display: inline-block;
  width: 17px;
  height: 17px;
  background-color: v-bind(mainColor);
  border-radius: 50%;
  margin: 0 4px;
  animation: bounce 2s infinite alternate;

  @media screen and (max-width: 768px) {
    width: calc(6vw / v-bind(numberOfColumns));
    height: calc(6vw / v-bind(numberOfColumns));
  }
}

.dot1 {
  animation-duration: 1s;
}

.dot2 {
  animation-duration: 1.5s;
}

.dot3 {
  animation-duration: 2s;
}

@keyframes bounce {
  0% {
    transform: translateY(0);
  }

  100% {
    transform: translateY(-16px);
  }
}
</style>
